import React, { useRef, useState, useEffect, useCallback } from 'react';
import { 
  LabelTemplate, 
  LabelElement, 
  mmToPx, 
  pxToMm, 
  ptToMm,
  SCREEN_DPI,
  LABEL_WIDTH_MM,
  LABEL_HEIGHT_MM,
  ShapeElement,
  ImageElement,
  IconElement,
} from '@/types/label';
import { generateBarcodeDataUrl, generateQRCodeDataUrl } from '@/lib/barcodes';
import { ResizeHandles, ResizeHandle } from './ResizeHandles';
import { AlignmentGuides, AlignmentGuide } from './AlignmentGuides';
import { icons as LucideIcons } from 'lucide-react';

interface LabelCanvasProps {
  template: LabelTemplate;
  selectedElementId: string | null;
  onSelectElement: (id: string | null) => void;
  onUpdateElement: (id: string, updates: Partial<LabelElement>) => void;
  resolveContent: (content: string) => string;
  showGrid?: boolean;
  gridSize?: number; // mm - defaults to 2mm
  scale?: number;
  onHistoryPush?: () => void;
  enableAlignment?: boolean;
}

const SNAP_THRESHOLD_MM = 0.5;

export const LabelCanvas: React.FC<LabelCanvasProps> = ({
  template,
  selectedElementId,
  onSelectElement,
  onUpdateElement,
  resolveContent,
  showGrid = true,
  gridSize = 2,
  scale = 3,
  onHistoryPush,
  enableAlignment = true,
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [resizing, setResizing] = useState<{ id: string; handle: ResizeHandle } | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0, mouseX: 0, mouseY: 0 });
  const [barcodeImages, setBarcodeImages] = useState<Record<string, string>>({});
  const [alignmentGuides, setAlignmentGuides] = useState<AlignmentGuide[]>([]);

  // Use template dimensions instead of constants
  const labelWidth = template.width;
  const labelHeight = template.height;
  
  const widthPx = mmToPx(labelWidth, SCREEN_DPI) * scale;
  const heightPx = mmToPx(labelHeight, SCREEN_DPI) * scale;
  const gridSizePx = mmToPx(gridSize, SCREEN_DPI) * scale;

  // Generate barcode images — only keeps entries for current elements
  useEffect(() => {
    const generateImages = async () => {
      const images: Record<string, string> = {};
      
      for (const element of template.elements) {
        if (element.visible === false) continue;
        
        const content = element.content ? resolveContent(element.content) : '';
        
        if (element.type === 'barcode') {
          const key = `${element.id}-${content}-${element.barScale || 2}-${element.barcodeType || 'code128'}-${element.barHeightRatio || 0.8}-${element.quietZone ?? 2}-${element.foregroundColor || ''}-${element.backgroundColor || ''}-${element.textSize || 8}`;
          images[key] = await generateBarcodeDataUrl(
            content, 
            element.size.width * scale, 
            element.size.height * scale,
            element.includeText,
            element.barScale || 2,
            element.barcodeType || 'code128',
            element.barHeightRatio || 0.8,
            element.quietZone ?? 2,
            element.foregroundColor,
            element.backgroundColor,
            element.textSize,
          );
        } else if (element.type === 'qrcode') {
          const key = `${element.id}-${content}-${element.foregroundColor || ''}-${element.backgroundColor || ''}`;
          const size = Math.min(element.size.width, element.size.height);
          images[key] = await generateQRCodeDataUrl(content, size * 10, element.foregroundColor, element.backgroundColor);
        }
      }
      
      // Replace entire state — old keys from removed elements are dropped
      setBarcodeImages(images);
    };
    
    generateImages();
  }, [template.elements, resolveContent, scale]);

  // Calculate snap guides
  const calculateSnapGuides = useCallback((
    currentId: string,
    x: number,
    y: number,
    width: number,
    height: number
  ): { x: number; y: number; guides: AlignmentGuide[] } => {
    const guides: AlignmentGuide[] = [];
    let snappedX = x;
    let snappedY = y;

    // Canvas edges
    const snapPoints = {
      vertical: [0, labelWidth / 2, labelWidth],
      horizontal: [0, labelHeight / 2, labelHeight],
    };

    // Add other elements' edges
    template.elements.forEach((el) => {
      if (el.id === currentId) return;
      snapPoints.vertical.push(el.position.x, el.position.x + el.size.width / 2, el.position.x + el.size.width);
      snapPoints.horizontal.push(el.position.y, el.position.y + el.size.height / 2, el.position.y + el.size.height);
    });

    // Check vertical snaps (x positions)
    const elementXPoints = [x, x + width / 2, x + width];
    for (const ex of elementXPoints) {
      for (const sp of snapPoints.vertical) {
        if (Math.abs(ex - sp) < SNAP_THRESHOLD_MM) {
          snappedX = x + (sp - ex);
          guides.push({ type: 'vertical', position: sp });
          break;
        }
      }
    }

    // Check horizontal snaps (y positions)
    const elementYPoints = [y, y + height / 2, y + height];
    for (const ey of elementYPoints) {
      for (const sp of snapPoints.horizontal) {
        if (Math.abs(ey - sp) < SNAP_THRESHOLD_MM) {
          snappedY = y + (sp - ey);
          guides.push({ type: 'horizontal', position: sp });
          break;
        }
      }
    }

    return { x: snappedX, y: snappedY, guides };
  }, [template.elements]);

  // Transform mouse coordinates to account for label rotation
  const transformMouseToCanvas = useCallback((clientX: number, clientY: number): { x: number; y: number } => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const rotation = template.labelRotation || 0;
    if (rotation === 0) {
      return { x: clientX - rect.left, y: clientY - rect.top };
    }
    
    // Translate to center, rotate inversely, translate back
    const rad = (-rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const dx = clientX - centerX;
    const dy = clientY - centerY;
    const rx = dx * cos - dy * sin;
    const ry = dx * sin + dy * cos;
    return { x: rx + widthPx / 2, y: ry + heightPx / 2 };
  }, [template.labelRotation, widthPx, heightPx]);

  const handleMouseDown = useCallback((e: React.MouseEvent, elementId: string) => {
    e.stopPropagation();
    const element = template.elements.find((el) => el.id === elementId);
    if (!element || element.locked) return;

    onSelectElement(elementId);
    
    if (!canvasRef.current) return;

    const { x, y } = transformMouseToCanvas(e.clientX, e.clientY);
    
    const elementX = mmToPx(element.position.x, SCREEN_DPI) * scale;
    const elementY = mmToPx(element.position.y, SCREEN_DPI) * scale;

    setDragOffset({ x: x - elementX, y: y - elementY });
    setDragging(elementId);
  }, [template.elements, onSelectElement, scale, transformMouseToCanvas]);

  const handleResizeStart = useCallback((elementId: string, handle: ResizeHandle, e: React.MouseEvent) => {
    e.stopPropagation();
    const element = template.elements.find((el) => el.id === elementId);
    if (!element || element.locked) return;

    setResizing({ id: elementId, handle });
    setResizeStart({
      x: element.position.x,
      y: element.position.y,
      width: element.size.width,
      height: element.size.height,
      mouseX: e.clientX,
      mouseY: e.clientY,
    });
  }, [template.elements]);

  // Transform mouse delta to account for label rotation
  const transformDelta = useCallback((deltaClientX: number, deltaClientY: number): { dx: number; dy: number } => {
    const rotation = template.labelRotation || 0;
    if (rotation === 0) return { dx: deltaClientX, dy: deltaClientY };
    
    const rad = (-rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return {
      dx: deltaClientX * cos - deltaClientY * sin,
      dy: deltaClientX * sin + deltaClientY * cos,
    };
  }, [template.labelRotation]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!canvasRef.current) return;

    if (dragging) {
      const element = template.elements.find((el) => el.id === dragging);
      if (!element) return;

      const { x: canvasX, y: canvasY } = transformMouseToCanvas(e.clientX, e.clientY);
      const x = canvasX - dragOffset.x;
      const y = canvasY - dragOffset.y;

      let xMm = Math.max(0, Math.min(pxToMm(x / scale, SCREEN_DPI), labelWidth - element.size.width));
      let yMm = Math.max(0, Math.min(pxToMm(y / scale, SCREEN_DPI), labelHeight - element.size.height));

      // Try alignment snap first (higher priority), fall back to grid snap
      if (enableAlignment) {
        const snap = calculateSnapGuides(dragging, xMm, yMm, element.size.width, element.size.height);
        if (snap.guides.length > 0) {
          // Alignment guides took effect — use snapped positions directly
          setAlignmentGuides(snap.guides);
          onUpdateElement(dragging, { position: { x: snap.x, y: snap.y } });
        } else {
          // No alignment guide hit — fall back to grid snap
          setAlignmentGuides([]);
          xMm = Math.round(xMm / gridSize) * gridSize;
          yMm = Math.round(yMm / gridSize) * gridSize;
          onUpdateElement(dragging, { position: { x: xMm, y: yMm } });
        }
      } else {
        xMm = Math.round(xMm / gridSize) * gridSize;
        yMm = Math.round(yMm / gridSize) * gridSize;
        onUpdateElement(dragging, { position: { x: xMm, y: yMm } });
      }
    }

    if (resizing) {
      const element = template.elements.find((el) => el.id === resizing.id);
      if (!element) return;

      const { dx: rawDeltaX, dy: rawDeltaY } = transformDelta(
        e.clientX - resizeStart.mouseX,
        e.clientY - resizeStart.mouseY
      );
      const deltaX = pxToMm(rawDeltaX / scale, SCREEN_DPI);
      const deltaY = pxToMm(rawDeltaY / scale, SCREEN_DPI);

      let newX = resizeStart.x;
      let newY = resizeStart.y;
      let newWidth = resizeStart.width;
      let newHeight = resizeStart.height;

      const { handle } = resizing;

      // Handle horizontal resizing
      if (handle.includes('w')) {
        newX = Math.max(0, Math.min(resizeStart.x + deltaX, resizeStart.x + resizeStart.width - 2));
        newWidth = resizeStart.width - (newX - resizeStart.x);
      }
      if (handle.includes('e')) {
        newWidth = Math.max(2, Math.min(resizeStart.width + deltaX, labelWidth - resizeStart.x));
      }

      // Handle vertical resizing
      if (handle.includes('n')) {
        newY = Math.max(0, Math.min(resizeStart.y + deltaY, resizeStart.y + resizeStart.height - 2));
        newHeight = resizeStart.height - (newY - resizeStart.y);
      }
      if (handle.includes('s')) {
        newHeight = Math.max(2, Math.min(resizeStart.height + deltaY, labelHeight - resizeStart.y));
      }

      // Snap to grid
      newX = Math.round(newX / gridSize) * gridSize;
      newY = Math.round(newY / gridSize) * gridSize;
      newWidth = Math.round(newWidth / gridSize) * gridSize;
      newHeight = Math.round(newHeight / gridSize) * gridSize;

      onUpdateElement(resizing.id, {
        position: { x: newX, y: newY },
        size: { width: Math.max(2, newWidth), height: Math.max(2, newHeight) },
      });
    }
  }, [dragging, resizing, dragOffset, resizeStart, onUpdateElement, scale, template.elements, calculateSnapGuides, transformMouseToCanvas, transformDelta]);

  const handleMouseUp = useCallback(() => {
    if (dragging || resizing) {
      onHistoryPush?.();
    }
    setDragging(null);
    setResizing(null);
    setAlignmentGuides([]);
  }, [dragging, resizing, onHistoryPush]);

  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    if (e.target === canvasRef.current) {
      onSelectElement(null);
    }
  }, [onSelectElement]);

  const getIconComponent = (iconName: string) => {
    const pascalCase = iconName
      .split('-')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join('');
    return LucideIcons[pascalCase as keyof typeof LucideIcons];
  };

  const renderElement = (element: LabelElement) => {
    if (element.visible === false) return null;
    
    const x = mmToPx(element.position.x, SCREEN_DPI) * scale;
    const y = mmToPx(element.position.y, SCREEN_DPI) * scale;
    const width = mmToPx(element.size.width, SCREEN_DPI) * scale;
    const height = mmToPx(element.size.height, SCREEN_DPI) * scale;
    const isSelected = selectedElementId === element.id;
    const isLocked = element.locked === true;
    const content = element.content ? resolveContent(element.content) : '';

    const rotation = element.rotation || 0;
    const baseStyle: React.CSSProperties = {
      position: 'absolute',
      left: x,
      top: y,
      width,
      height,
      cursor: isLocked ? 'not-allowed' : 'move',
      border: isSelected ? '2px solid hsl(var(--selection))' : '1px dashed transparent',
      boxSizing: 'border-box',
      backgroundColor: isSelected ? 'hsla(var(--selection), 0.05)' : 'transparent',
      opacity: isLocked ? 0.7 : 1,
      transform: rotation ? `rotate(${rotation}deg)` : undefined,
      transformOrigin: 'center center',
    };

    const handleElementResizeStart = (handle: ResizeHandle, e: React.MouseEvent) => {
      handleResizeStart(element.id, handle, e);
    };

    if (element.type === 'text') {
      // Convert pt to mm, then mm to px for accurate screen rendering
      const fontSizePx = mmToPx(ptToMm(element.fontSize), SCREEN_DPI) * scale;
      
      return (
        <div
          key={element.id}
          style={{
            ...baseStyle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: element.textAlign === 'center' ? 'center' : 
                           element.textAlign === 'right' ? 'flex-end' : 'flex-start',
            fontSize: fontSizePx,
            fontFamily: element.fontFamily,
            fontWeight: element.fontWeight,
            color: element.color || 'hsl(var(--element-text))',
            overflow: 'hidden',
            wordBreak: 'break-word',
            lineHeight: 1.2,
          }}
          onMouseDown={(e) => handleMouseDown(e, element.id)}
        >
          {content}
          {isSelected && <ResizeHandles onResizeStart={handleElementResizeStart} isLocked={isLocked} />}
        </div>
      );
    }

    if (element.type === 'barcode') {
      const imageKey = `${element.id}-${content}-${element.barScale || 2}-${element.barcodeType || 'code128'}-${element.barHeightRatio || 0.8}-${element.quietZone ?? 2}-${element.foregroundColor || ''}-${element.backgroundColor || ''}-${element.textSize || 8}`;
      return (
        <div
          key={element.id}
          style={baseStyle}
          onMouseDown={(e) => handleMouseDown(e, element.id)}
        >
          {barcodeImages[imageKey] && (
            <img 
              src={barcodeImages[imageKey]} 
              alt="Barcode"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              draggable={false}
            />
          )}
          {isSelected && <ResizeHandles onResizeStart={handleElementResizeStart} isLocked={isLocked} />}
        </div>
      );
    }

    if (element.type === 'qrcode') {
      const imageKey = `${element.id}-${content}-${element.foregroundColor || ''}-${element.backgroundColor || ''}`;
      return (
        <div
          key={element.id}
          style={baseStyle}
          onMouseDown={(e) => handleMouseDown(e, element.id)}
        >
          {barcodeImages[imageKey] && (
            <img 
              src={barcodeImages[imageKey]} 
              alt="QR Code"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              draggable={false}
            />
          )}
          {isSelected && <ResizeHandles onResizeStart={handleElementResizeStart} isLocked={isLocked} />}
        </div>
      );
    }

    if (element.type === 'image') {
      const imgElement = element as ImageElement;
      return (
        <div
          key={element.id}
          style={{
            ...baseStyle,
            overflow: 'hidden',
            borderRadius: imgElement.borderRadius || 0,
          }}
          onMouseDown={(e) => handleMouseDown(e, element.id)}
        >
          {imgElement.src ? (
            <img 
              src={imgElement.src} 
              alt="Image"
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: imgElement.objectFit || 'contain',
              }}
              draggable={false}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground text-xs">
              No image
            </div>
          )}
          {isSelected && <ResizeHandles onResizeStart={handleElementResizeStart} isLocked={isLocked} />}
        </div>
      );
    }

    if (element.type === 'shape') {
      const shapeElement = element as ShapeElement;
      return (
        <div
          key={element.id}
          style={{
            ...baseStyle,
            backgroundColor: 'transparent',
          }}
          onMouseDown={(e) => handleMouseDown(e, element.id)}
        >
          {shapeElement.shapeType === 'rectangle' && (
            <div
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: shapeElement.fillColor || 'transparent',
                border: `${shapeElement.strokeWidth || 1}px solid ${shapeElement.strokeColor || '#000'}`,
                borderRadius: shapeElement.borderRadius || 0,
              }}
            />
          )}
          {shapeElement.shapeType === 'circle' && (
            <div
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: shapeElement.fillColor || 'transparent',
                border: `${shapeElement.strokeWidth || 1}px solid ${shapeElement.strokeColor || '#000'}`,
                borderRadius: '50%',
              }}
            />
          )}
          {shapeElement.shapeType === 'line' && (
            <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0 }}>
              <line
                x1="0"
                y1="0"
                x2="100%"
                y2="100%"
                stroke={shapeElement.strokeColor || '#000'}
                strokeWidth={shapeElement.strokeWidth || 1}
              />
            </svg>
          )}
          {shapeElement.shapeType === 'triangle' && (
            <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
              <polygon
                points="50,0 0,100 100,100"
                fill={shapeElement.fillColor || 'transparent'}
                stroke={shapeElement.strokeColor || '#000'}
                strokeWidth={shapeElement.strokeWidth || 1}
              />
            </svg>
          )}
          {isSelected && <ResizeHandles onResizeStart={handleElementResizeStart} isLocked={isLocked} />}
        </div>
      );
    }

    if (element.type === 'icon') {
      const iconElement = element as IconElement;
      const IconComponent = getIconComponent(iconElement.iconName);
      return (
        <div
          key={element.id}
          style={{
            ...baseStyle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseDown={(e) => handleMouseDown(e, element.id)}
        >
          {IconComponent && (
            <IconComponent 
              style={{ 
                width: '100%', 
                height: '100%', 
                color: iconElement.color || '#000' 
              }} 
            />
          )}
          {isSelected && <ResizeHandles onResizeStart={handleElementResizeStart} isLocked={isLocked} />}
        </div>
      );
    }

    return null;
  };

  const isLabelRotated90or270 = template.labelRotation === 90 || template.labelRotation === 270;
  const outerWidth = isLabelRotated90or270 ? heightPx : widthPx;
  const outerHeight = isLabelRotated90or270 ? widthPx : heightPx;

  return (
    <div className="flex items-center justify-center p-8 xs:p-10 sm:p-16 bg-workspace min-h-[180px] sm:min-h-[400px] w-full overflow-auto">
      <div className="relative shrink-0" style={{ minWidth: 'fit-content', width: outerWidth, height: outerHeight }}>
        {/* Inner wrapper that rotates rulers + canvas together */}
        <div
          className="absolute"
          style={{
            width: widthPx,
            height: heightPx,
            top: '50%',
            left: '50%',
            transform: `translate(-50%, -50%)${template.labelRotation ? ` rotate(${template.labelRotation}deg)` : ''}`,
            transformOrigin: 'center center',
          }}
        >
          {/* Ruler - Top */}
          <div 
            className="absolute -top-5 sm:-top-6 left-0 h-4 sm:h-5 bg-ruler border border-border rounded-t-sm overflow-hidden hidden xs:block"
            style={{ width: widthPx }}
          >
            {Array.from({ length: Math.ceil(labelWidth / 5) + 1 }).map((_, i) => (
              <div 
                key={i}
                className="absolute text-[8px] sm:text-[9px] text-muted-foreground"
                style={{ left: mmToPx(i * 5, SCREEN_DPI) * scale - 4 }}
              >
                {i * 5}
              </div>
            ))}
          </div>

          {/* Ruler - Left */}
          <div 
            className="absolute -left-5 sm:-left-6 top-0 w-4 sm:w-5 bg-ruler border border-border rounded-l-sm overflow-hidden hidden xs:block"
            style={{ height: heightPx }}
          >
            {Array.from({ length: Math.ceil(labelHeight / 5) + 1 }).map((_, i) => (
              <div 
                key={i}
                className="absolute text-[8px] sm:text-[9px] text-muted-foreground -rotate-90"
                style={{ top: mmToPx(i * 5, SCREEN_DPI) * scale - 4, left: -2 }}
              >
                {i * 5}
              </div>
            ))}
          </div>

          {/* Canvas */}
          <div
            ref={canvasRef}
            className="relative bg-canvas border-2 border-border shadow-lg touch-pan-x touch-pan-y"
            style={{ 
              width: widthPx, 
              height: heightPx,
              backgroundImage: showGrid 
                ? `linear-gradient(to right, hsl(var(--grid)) 1px, transparent 1px),
                   linear-gradient(to bottom, hsl(var(--grid)) 1px, transparent 1px)`
                : 'none',
              backgroundSize: `${gridSizePx}px ${gridSizePx}px`,
            }}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onClick={handleCanvasClick}
          >
            <AlignmentGuides guides={alignmentGuides} scale={scale} />
            {template.elements.map(renderElement)}
          </div>

          {/* Size indicator */}
          <div className="absolute -bottom-6 sm:-bottom-8 left-0 right-0 text-center text-[10px] sm:text-xs text-muted-foreground">
            {labelWidth} × {labelHeight} mm
          </div>
        </div>
      </div>
    </div>
  );
};
